import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  Clock,
  ChevronDown,
  FileText,
  Link,
  Music,
  Video,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  BookOpen,
  Folder,
  FolderPlus,
  Home
} from 'lucide-react';
import api from '../../../utils/api';
import MediaViewer from '../../../components/MediaViewer';
import AudioViewer from '../../../components/AudioViewer';
import VideoViewer from '../../../components/VideoViewer';

const ServicesSection = ({
  services,
  expandedServices,
  setExpandedServices,
  setEditingService,
  setServiceForm,
  setShowServiceModal,
  handleEditService,
  handleDeleteService,
  toggleExpanded,
  // Modal props
  showServiceModal,
  editingService,
  serviceForm,
  serviceLoading,
  handleServiceSubmit,
  handleServiceInputChange,
  // Custom dialog function
  showCustomDialog,
  showConfirmationDialog,
  // Resource folder navigation
  currentResourceFolder,
  handleEnterResourceFolder,
  handleExitResourceFolder
}) => {
  // Resources state
  const [resources, setResources] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceForm, setResourceForm] = useState({
    resourceName: '',
    resourceType: 'Audio',
    resourceUrl: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderLoading, setFolderLoading] = useState(false);
  
  // MediaViewer state
  const [showMediaViewer, setShowMediaViewer] = useState(false);
  const [viewingResource, setViewingResource] = useState(null);
  
  // AudioViewer state
  const [showAudioViewer, setShowAudioViewer] = useState(false);
  const [viewingAudioResource, setViewingAudioResource] = useState(null);
  
  // VideoViewer state
  const [showVideoViewer, setShowVideoViewer] = useState(false);
  const [viewingVideoResource, setViewingVideoResource] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  
  // Sorting state
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  // Fetch all folders
  const fetchFolders = async () => {
    try {
      const response = await api.get('/api/service-resources/folders');
      setFolders(response.data);
    } catch (error) {
      console.error('Error fetching folders:', error);
      setFolders([]);
    }
  };

  // Fetch resources for a specific folder
  const fetchResources = async (folderId) => {
    try {
      setLoadingResources(true);
      const response = await api.get(`/api/service-resources/folders/${folderId}/resources`);
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
    } finally {
      setLoadingResources(false);
    }
  };

  // Load folders on component mount
  useEffect(() => {
    fetchFolders();
  }, []);

  // Load resources when a folder is selected
  useEffect(() => {
    if (currentResourceFolder) {
      fetchResources(currentResourceFolder.id);
    } else {
      setResources([]);
    }
  }, [currentResourceFolder]);

  // Handle creating a new folder
  const handleCreateFolder = async () => {
    try {
      setFolderLoading(true);
      await api.post('/api/service-resources/folders', { folderName });
      await fetchFolders();
      setShowFolderModal(false);
      setFolderName('');
      showCustomDialog('Folder created successfully!');
    } catch (error) {
      console.error('Error creating folder:', error);
      showCustomDialog('Failed to create folder', 'error');
    } finally {
      setFolderLoading(false);
    }
  };

  // Handle deleting a folder
  const handleDeleteFolder = (folderId, folderName) => {
    const deleteAction = async () => {
      try {
        await api.delete(`/api/service-resources/folders/${folderId}`);
        await fetchFolders();
        if (currentResourceFolder?.id === folderId) {
          handleExitResourceFolder();
        }
        showCustomDialog('Folder deleted successfully!');
      } catch (error) {
        console.error('Error deleting folder:', error);
        showCustomDialog('Failed to delete folder', 'error');
      }
    };

    showConfirmationDialog(`Are you sure you want to delete "${folderName}" and all its resources?`, deleteAction);
  };

  const handleResourceInputChange = (e) => {
    const { name, value } = e.target;
    setResourceForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle viewing resource with appropriate viewer
  const handleViewResource = (resource) => {
    if (resource.resource_type === 'Audio') {
      setViewingAudioResource(resource);
      setShowAudioViewer(true);
    } else if (resource.resource_type === 'Video') {
      setViewingVideoResource(resource);
      setShowVideoViewer(true);
    } else {
      setViewingResource(resource);
      setShowMediaViewer(true);
    }
  };

  const closeMediaViewer = () => {
    setViewingResource(null);
    setShowMediaViewer(false);
  };

  const closeAudioViewer = () => {
    setViewingAudioResource(null);
    setShowAudioViewer(false);
  };

  const closeVideoViewer = () => {
    setViewingVideoResource(null);
    setShowVideoViewer(false);
  };

  // Sorting logic
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const sortedResources = [...resources].sort((a, b) => {
    if (!sortField) return 0;
    
    let aValue = '';
    let bValue = '';
    
    if (sortField === 'resource_name') {
      aValue = a.resource_name?.toLowerCase() || '';
      bValue = b.resource_name?.toLowerCase() || '';
    } else if (sortField === 'resource_type') {
      aValue = a.resource_type?.toLowerCase() || '';
      bValue = b.resource_type?.toLowerCase() || '';
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination logic (applied after sorting)
  const totalPages = Math.ceil(sortedResources.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentResources = sortedResources.slice(startIndex, endIndex);
  
  // Helper function to render sort icon
  const renderSortIcon = (field) => {
    if (!sortField || sortField !== field) {
      return null; // Don't show any icon when no sorting is active or for inactive columns
    }
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4 text-blue-600" /> : 
      <ChevronDown className="w-4 h-4 text-blue-600" />;
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Reset to first page when resources change
  useEffect(() => {
    setCurrentPage(1);
  }, [resources.length]);
  
  // Reset sorting when resources change
  useEffect(() => {
    setSortField('');
    setSortDirection('asc');
  }, [resources.length]);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleAddResource = () => {
    setEditingResource(null);
    setResourceForm({
      resourceName: '',
      resourceType: 'Audio',
      resourceUrl: ''
    });
    setSelectedFile(null);
    setShowResourceModal(true);
  };

  const handleEditResource = (resource) => {
    setEditingResource(resource);
    setResourceForm({
      resourceName: resource.resource_name,
      resourceType: resource.resource_type,
      resourceUrl: resource.resource_url || ''
    });
    setSelectedFile(null);
    setShowResourceModal(true);
  };

  const handleResourceSubmit = async (e) => {
    e.preventDefault();
    setResourceLoading(true);

    try {
      if (resourceForm.resourceType === 'Documents') {
        // Handle document upload
        if (editingResource) {
          // Update existing document
          await updateDocumentResource();
        } else {
          // Create new document
          await createDocumentResource();
        }
      } else if (resourceForm.resourceType === 'Audio') {
        // Handle audio file upload
        if (editingResource) {
          // Update existing audio - for now just update name, file replacement can be added later
          await updateUrlResource();
        } else {
          // Create new audio
          await createAudioResource();
        }
      } else {
        // Handle URL-based resources (Video)
        if (editingResource) {
          // Update existing resource
          await updateUrlResource();
        } else {
          // Create new resource
          await createUrlResource();
        }
      }

      // Refresh resources list
      await fetchResources(currentResourceFolder.id);
      setShowResourceModal(false);
      showCustomDialog('Resource saved successfully!');
      // Reset to first page after adding/updating resource
      setCurrentPage(1);
    } catch (error) {
      console.error('Error saving resource:', error);
      // Close the modal first, then show error dialog
      setShowResourceModal(false);
      // Reset form state
      setResourceForm({
        resourceName: '',
        resourceType: 'Audio',
        resourceUrl: ''
      });
      setSelectedFile(null);
      setEditingResource(null);
      // Use setTimeout to ensure modal closes before showing error dialog
      setTimeout(() => {
        showCustomDialog('Failed to save resource', 'error');
      }, 100);
    } finally {
      setResourceLoading(false);
    }
  };

  const createUrlResource = async () => {
    await api.post('/api/service-resources', {
      resourceName: resourceForm.resourceName,
      resourceType: resourceForm.resourceType,
      resourceUrl: resourceForm.resourceUrl,
      folderId: currentResourceFolder.id
    });
  };

  const updateUrlResource = async () => {
    await api.put(`/api/service-resources/${editingResource.id}`, {
      resourceName: resourceForm.resourceName,
      resourceType: resourceForm.resourceType,
      resourceUrl: resourceForm.resourceUrl
    });
  };

  const createDocumentResource = async () => {
    if (!selectedFile) {
      throw new Error('Please select a file');
    }

    try {
      // Step 1: Get upload signature
      const signatureResponse = await api.post('/api/service-resources/upload-document-signature', {
        resourceName: resourceForm.resourceName,
        fileType: selectedFile.type
      });

      const { signature, timestamp, cloudName, apiKey, uploadParams } = signatureResponse.data;

      // Step 2: Upload directly to Cloudinary
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', apiKey);
      
      // Add upload parameters (exclude resource_type as it's in the URL)
      Object.keys(uploadParams).forEach(key => {
        if (key !== 'timestamp' && key !== 'resource_type') {
          formData.append(key, uploadParams[key]);
        }
      });

      // Use the correct resource type endpoint
      const resourceType = uploadParams.resource_type || 'auto';
      const cloudinaryResponse = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`, {
        method: 'POST',
        body: formData
      });

      if (!cloudinaryResponse.ok) {
        const errorText = await cloudinaryResponse.text();
        console.error('Cloudinary error:', errorText);
        throw new Error('Failed to upload file to Cloudinary');
      }

      const cloudinaryResult = await cloudinaryResponse.json();

      // Step 3: Save file info to database
      await api.post('/api/service-resources/save-document', {
        resourceName: resourceForm.resourceName,
        resourceUrl: cloudinaryResult.secure_url,
        publicId: cloudinaryResult.public_id,
        fileName: cloudinaryResult.original_filename || selectedFile.name,
        folderId: currentResourceFolder.id
      });
    } catch (error) {
      console.error('Document upload error:', error);
      throw new Error('Failed to upload document. Please try again.');
    }
  };

  const createAudioResource = async () => {
    if (!selectedFile) {
      throw new Error('Please select an audio file');
    }

    try {
      // Step 1: Get upload signature
      const signatureResponse = await api.post('/api/service-resources/upload-audio-signature', {
        resourceName: resourceForm.resourceName
      });

      const { signature, timestamp, cloudName, apiKey, uploadParams } = signatureResponse.data;

      // Step 2: Upload directly to Cloudinary
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', apiKey);
      
      // Add upload parameters
      Object.keys(uploadParams).forEach(key => {
        if (key !== 'timestamp') {
          formData.append(key, uploadParams[key]);
        }
      });

      const cloudinaryResponse = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`, {
        method: 'POST',
        body: formData
      });

      if (!cloudinaryResponse.ok) {
        throw new Error('Failed to upload audio file to Cloudinary');
      }

      const cloudinaryResult = await cloudinaryResponse.json();

      // Step 3: Save file info to database
      await api.post('/api/service-resources/save-audio', {
        resourceName: resourceForm.resourceName,
        resourceUrl: cloudinaryResult.secure_url,
        publicId: cloudinaryResult.public_id,
        fileName: cloudinaryResult.original_filename || selectedFile.name,
        folderId: currentResourceFolder.id
      });
    } catch (error) {
      console.error('Audio upload error:', error);
      throw new Error('Failed to upload audio file. Please try again.');
    }
  };

  const updateDocumentResource = async () => {
    if (selectedFile) {
      // Replace document file
      const formData = new FormData();
      formData.append('document', selectedFile);

      await api.put(`/api/service-resources/${editingResource.id}/replace-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }

    // Update resource name and type
    await api.put(`/api/service-resources/${editingResource.id}`, {
      resourceName: resourceForm.resourceName,
      resourceType: resourceForm.resourceType,
      resourceUrl: editingResource.resource_url // Keep existing URL
    });
  };

  const handleDeleteResource = (resourceId, resourceName) => {
    const deleteAction = async () => {
      try {
        await api.delete(`/api/service-resources/${resourceId}`);
        await fetchResources(currentResourceFolder.id);
        showCustomDialog('Resource deleted successfully!');
        // Reset to first page if current page becomes empty
        const newTotalPages = Math.ceil((resources.length - 1) / itemsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) {
          setCurrentPage(newTotalPages);
        }
      } catch (error) {
        console.error('Error deleting resource:', error);
        showCustomDialog('Failed to delete resource', 'error');
      }
    };

    showConfirmationDialog(`Are you sure you want to delete "${resourceName}"?`, deleteAction);
  };
  const regularServices = services.filter(service => service.category === 'regular');
  const specialServices = services.filter(service => service.category === 'special');

  const renderServiceRow = (service, index) => {
    const isExpanded = expandedServices?.has(service.id);
    
    return (
      <React.Fragment key={service.id}>
        <tr className="bg-white border-b border-gray-200 hover:bg-gray-50">
          {/* Mobile expand/collapse button */}
          <td className="px-3 py-4 text-center md:hidden">
            <button
              onClick={() => toggleExpanded(service.id, expandedServices, setExpandedServices)}
              className="text-gray-500 hover:text-gray-700"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          </td>
          
          {/* Service Name */}
          <td className="px-3 py-4 text-sm text-gray-900 font-medium">
            {service.name}
          </td>
          
          {/* Day - Hidden on mobile, always shown on desktop */}
          <td className="px-3 py-4 text-sm text-gray-600 hidden md:table-cell">
            {service.day}
          </td>
          
          {/* Time - Hidden on mobile, always shown on desktop */}
          <td className="px-3 py-4 text-sm text-gray-600 hidden md:table-cell">
            {service.time}
          </td>
          
          {/* Language - Hidden on mobile, always shown on desktop */}
          <td className="px-3 py-4 text-sm text-gray-600 hidden md:table-cell">
            {service.language || '-'}
          </td>
          
          {/* Actions */}
          <td className="px-3 py-4 text-sm">
            <div className="flex gap-2">
              <button
                onClick={() => handleEditService(service)}
                className="bg-blue-600 text-white p-1.5 rounded hover:bg-blue-700 transition-colors"
                title="Edit Service"
              >
                <Edit className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleDeleteService(service.id)}
                className="bg-red-600 text-white p-1.5 rounded hover:bg-red-700 transition-colors"
                title="Delete Service"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </td>
        </tr>
        
        {/* Mobile expanded content */}
        {isExpanded && (
          <tr className="md:hidden bg-gray-50">
            <td colSpan="6" className="px-3 py-3">
              <div className="space-y-2 text-sm">
                <div><strong>Day:</strong> {service.day}</div>
                <div><strong>Time:</strong> {service.time}</div>
                <div><strong>Language:</strong> {service.language || '-'}</div>
                {service.description && (
                  <div><strong>Description:</strong> {service.description}</div>
                )}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderServiceTable = (serviceList, title, emptyMessage) => (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4">
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      
      {serviceList.length === 0 ? (
        <div className="text-center py-12">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-gray-600 mb-2">No Services</h4>
          <p className="text-gray-500">{emptyMessage}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:hidden">
                  
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service Name
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  Day
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  Time
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  Language
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {serviceList.map((service, index) => renderServiceRow(service, index))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* Only show Manage Services section when not in a folder */}
      {!currentResourceFolder && (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0 mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Manage Services</h2>
            <p className="text-gray-600 mt-1">Add, edit, and manage church services</p>
          </div>
          <button
            onClick={() => handleEditService()}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Service
          </button>
        </div>
      )}

      {/* Only show services tables when not in a folder */}
      {!currentResourceFolder && (
        <>
          {/* Sunday Services Table */}
          {renderServiceTable(
            regularServices,
            "Sunday Services",
            "No Sunday services have been added yet."
          )}

          {/* Special Services Table */}
          {renderServiceTable(
            specialServices,
            "Special Day Services",
            "No special services have been added yet."
          )}
        </>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Manage Service Resources</h2>
          {!currentResourceFolder && (<p className="text-gray-600 mt-1">Manage audio, video, and document resources</p>)}
          {currentResourceFolder && (
            <div className="flex items-center text-sm text-gray-600 mt-1">
              <button
                onClick={handleExitResourceFolder}
                className="flex items-center hover:text-gray-800"
              >
                <Home className="w-4 h-4 mr-1" />
                Service Resources
              </button>
              <ChevronRight className="w-4 h-4 mx-1" />
              <span className="font-medium">{currentResourceFolder.folder_name}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {!currentResourceFolder && (
            <button
              onClick={() => setShowFolderModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <FolderPlus className="w-4 h-4" />
              Create Folder
            </button>
          )}
          {currentResourceFolder && (
            <>
              <button
                onClick={handleAddResource}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Resource
              </button>
            </>
          )}
        </div>
      </div>

      {/* Folder List - only show when not inside a folder */}
      {!currentResourceFolder && (
        folders.length === 0 ? (
          <div className="text-center py-12">
            <Folder className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-600 mb-2">No Folders</h4>
            <p className="text-gray-500">Create a folder to start organizing your resources.</p>
          </div>
        ) : (
          <div className="mb-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer border relative group"
                >
                  <div className="p-4">
                    <div className="flex flex-col items-center space-y-2">
                      <div
                        onClick={() => handleEnterResourceFolder(folder)}
                        className="flex flex-col items-center space-y-2 flex-1"
                      >
                        <Folder className="w-12 h-12 text-blue-600" />
                        <span className="text-sm font-medium text-gray-900 text-center break-words">
                          {folder.folder_name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {folder.itemCount || 0} {Number(folder.itemCount) === 1 ? 'item' : 'items'}
                        </span>
                      </div>
                      {(!folder.itemCount || folder.itemCount === 0) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFolder(folder.id, folder.folder_name);
                          }}
                          className="flex space-x-1 pt-1 border-t border-gray-100 w-full justify-center text-red-600 hover:text-red-700 p-1"
                          title="Delete Folder"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* Service Resources Section - only show when inside a folder */}
      {currentResourceFolder && (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4">
            {currentResourceFolder.folder_name} - Resources
          </div>

          {/* Resources Table */}
          {loadingResources ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading resources...</p>
            </div>
          ) : resources.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-gray-600 mb-2">No Resources</h4>
              <p className="text-gray-500">No resources have been added to this folder yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('resource_name')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Resource Name</span>
                        {renderSortIcon('resource_name')}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('resource_type')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Type</span>
                        {renderSortIcon('resource_type')}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      View File
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentResources.map((resource) => (
                    <tr key={resource.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {resource.resource_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {resource.resource_type}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {resource.resource_type === 'Documents' ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewResource(resource)}
                              className="text-green-600 hover:text-green-800 flex items-center gap-1"
                              title="View in viewer"
                            >
                              <BookOpen className="w-4 h-4" />
                              View {resource.resource_name}
                            </button>
                          </div>
                        ) : resource.resource_type === 'Audio' ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewResource(resource)}
                              className="text-purple-600 hover:text-purple-800 flex items-center gap-1"
                              title="Play audio"
                            >
                              <Music className="w-4 h-4" />
                              Play {resource.resource_name}
                            </button>
                          </div>
                        ) : resource.resource_type === 'Video' ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewResource(resource)}
                              className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              title="Play video"
                            >
                              <Video className="w-4 h-4" />
                              Play {resource.resource_name}
                            </button>
                          </div>
                        ) : (
                          <a
                            href={resource.resource_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <Link className="w-4 h-4" />
                            View Link
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditResource(resource)}
                            className="bg-blue-600 text-white p-1.5 rounded hover:bg-blue-700 transition-colors"
                            title="Edit Resource"
                          >
                            <Edit className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => handleDeleteResource(resource.id, resource.resource_name)}
                            className="bg-red-600 text-white p-1.5 rounded hover:bg-red-700 transition-colors"
                            title="Delete Resource"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loadingResources && resources.length > itemsPerPage && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(endIndex, sortedResources.length)}</span> of{' '}
                    <span className="font-medium">{sortedResources.length}</span> resources
                    {sortField && (
                      <span className="text-blue-600 ml-2">
                        (sorted by {sortField === 'resource_name' ? 'name' : 'type'} {sortDirection === 'asc' ? '↑' : '↓'})
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    {[...Array(totalPages)].map((_, index) => {
                      const page = index + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Service Modal */}
      {showServiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-auto max-h-[97%] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">
              {editingService ? 'Edit Service' : 'Add New Service'}
            </h3>
            
            <form onSubmit={handleServiceSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={serviceForm.name}
                  onChange={handleServiceInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="e.g., Holy Qurbana"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day *
                </label>
                <input
                  type="text"
                  name="day"
                  value={serviceForm.day}
                  onChange={handleServiceInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="e.g., Sunday, First Saturday"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time *
                </label>
                <input
                  type="text"
                  name="time"
                  value={serviceForm.time}
                  onChange={handleServiceInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="e.g., 9:00 AM"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  name="description"
                  value={serviceForm.description}
                  onChange={handleServiceInputChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="Brief description of the service"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language (Optional)
                </label>
                <input
                  type="text"
                  name="language"
                  value={serviceForm.language}
                  onChange={handleServiceInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  placeholder="e.g., Malayalam & English"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="category"
                      value="regular"
                      checked={serviceForm.category === 'regular'}
                      onChange={handleServiceInputChange}
                      className="mr-2 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-gray-700">Sunday Service</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="category"
                      value="special"
                      checked={serviceForm.category === 'special'}
                      onChange={handleServiceInputChange}
                      className="mr-2 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-gray-700">Special Service</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowServiceModal(false);
                    setEditingService(null);
                    setServiceForm({
                      name: '',
                      day: '',
                      time: '',
                      description: '',
                      language: '',
                      category: 'regular'
                    });
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={serviceLoading}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {serviceLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {serviceLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Resource Modal */}
      {showResourceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-auto max-h-[97%] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">
              {editingResource ? 'Edit Resource' : 'Add New Resource'}
            </h3>
            
            <form onSubmit={handleResourceSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resource Name *
                </label>
                <input
                  type="text"
                  name="resourceName"
                  value={resourceForm.resourceName}
                  onChange={handleResourceInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="e.g., Service Audio Recording"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resource Type *
                </label>
                <select
                  name="resourceType"
                  value={resourceForm.resourceType}
                  onChange={handleResourceInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                >
                  <option value="Audio">Audio</option>
                  <option value="Video">Video</option>
                  <option value="Documents">Documents</option>
                </select>
              </div>

              {/* Conditional Fields Based on Resource Type */}
              {resourceForm.resourceType === 'Documents' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {editingResource ? 'Change File' : 'Upload File'} *
                  </label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.jpg,.jpeg,.png,.gif,.webp"
                    required={!editingResource}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Supported formats: PDF, Word, Excel, Text, and Image files (max 50MB)
                  </p>
                  {editingResource && editingResource.file_name && (
                    <p className="text-sm text-blue-600 mt-2">
                      Current file: {editingResource.file_name}
                    </p>
                  )}
                </div>
              ) : resourceForm.resourceType === 'Audio' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {editingResource ? 'Change Audio File' : 'Upload Audio File'} *
                  </label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    accept=".mp3,.wav,.ogg,.m4a,.aac,.flac"
                    required={!editingResource}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Supported formats: MP3, WAV, OGG, M4A, AAC, FLAC (max 50MB)
                  </p>
                  {editingResource && editingResource.file_name && (
                    <p className="text-sm text-blue-600 mt-2">
                      Current file: {editingResource.file_name}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resource URL *
                  </label>
                  <input
                    type="url"
                    name="resourceUrl"
                    value={resourceForm.resourceUrl}
                    onChange={handleResourceInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="https://example.com/video-link"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Paste the URL of your video resource (YouTube, Vimeo, etc.)
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowResourceModal(false);
                    setEditingResource(null);
                    setResourceForm({
                      resourceName: '',
                      resourceType: 'Audio',
                      resourceUrl: ''
                    });
                    setSelectedFile(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resourceLoading}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {resourceLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {resourceLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MediaViewer for Document Viewing */}
      <MediaViewer
        isOpen={showMediaViewer}
        onClose={closeMediaViewer}
        resource={viewingResource ? {
          resource_url: viewingResource.resource_url,
          resource_name: viewingResource.resource_name,
          file_name: viewingResource.file_name
        } : null}
        title={viewingResource?.resource_name || ''}
      />

      {/* AudioViewer for Audio Playback */}
      <AudioViewer
        isOpen={showAudioViewer}
        onClose={closeAudioViewer}
        resource={viewingAudioResource ? {
          resource_url: viewingAudioResource.resource_url,
          resource_name: viewingAudioResource.resource_name,
          file_name: viewingAudioResource.file_name
        } : null}
        title={viewingAudioResource?.resource_name || ''}
      />

      {/* VideoViewer for Video Playback */}
      <VideoViewer
        isOpen={showVideoViewer}
        onClose={closeVideoViewer}
        resource={viewingVideoResource ? {
          resource_url: viewingVideoResource.resource_url,
          resource_name: viewingVideoResource.resource_name,
          file_name: viewingVideoResource.file_name
        } : null}
        title={viewingVideoResource?.resource_name || ''}
      />

      {/* Create Folder Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto">
            <h3 className="text-xl font-bold mb-4">Create New Folder</h3>

            <form onSubmit={(e) => {
              e.preventDefault();
              handleCreateFolder();
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Folder Name *
                </label>
                <input
                  type="text"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="e.g., 2024 Services"
                  required
                />
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowFolderModal(false);
                    setFolderName('');
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={folderLoading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {folderLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <FolderPlus className="w-4 h-4" />
                  )}
                  {folderLoading ? 'Creating...' : 'Create Folder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesSection;